import { Seaport } from "@opensea/seaport-js";
import { CreateOrderInput } from "@opensea/seaport-js/lib/types";
import axios from "axios";
import { ethers } from "ethers";
import { forwardRef, useState } from "react";
import styled from "styled-components";
import MasterCopy from "../../common/models/MasterCopy";
import CloseCircle from "../../components/Buttons/CloseCircle";
import SimpleButton from "../../components/Buttons/SimpleButton";
import Column from "../../components/Column/Column";
import ErrorInputWithIcon from "../../components/Inputs/ErrorInputWithIcon";
import InputWithLabel from "../../components/InputWithLabel/InputWithLabel";
import { Modal } from "../../components/Modal/Modal";
import {
  CircularSVG,
  CustomCircle
} from "../../components/ProgressBar/CircluarSVG";
import Row from "../../components/Row/Row";
import { BlockChainErrorText } from "../../components/Text/BlockchainErrorText";
import InputErrorText from "../../components/Text/InputErrorText";
import { ProgressText } from "../../components/Text/ProgressText";
import RequiredText from "../../components/Text/RequiredText";
import Text from "../../components/Text/Text";
import { useAuth } from "../../hooks/useAuth";
import close_circle from "../../imgs/close-circle.svg";
import eth_icon from "../../imgs/eth_icon_blue.svg";
import { getSelectedProvider } from "../../utils";

interface ListToMarketplaceModalProps {
  theme: string;
  song: MasterCopy;
  showListToMarketplaceModal: boolean;
  onListToMarketplaceCloseClick: any;
  onListSuccess: any;
}

const wrappedEthTestNetAddress = "0xA6FA4fB5f76172d178d61B04b0ecd319C5d1C0aa";
const wrappedEthMainnetAddress = "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619";

const SongCheckoutContainer = styled("div")`
  display: flex;
  border: 1px solid #ffffff90;
  border-radius: 12px;
  width: 100%;
`;

const SongCheckoutImg = styled("img")`
  width: 200px;
  height: 150px;
  position: relative;
  display: inline-block;
  overflow: hidden;
  margin: 0;
  border-radius: 12px;
`;
const RequiredEthText = styled(RequiredText)`
  &:after {
    content: "* (ETH)";
  }
`;

const CheckoutPriceContainer = styled("div")`
  color: #03bbf5;
  border: 1px solid #03bbf5;
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const DisabledButton = styled(SimpleButton)`
  background: grey;
  margin-left: auto;
`;

const ModalContainer = styled(Column)`
  gap: 1rem;
  padding-inline: 2rem;
  position: relative;
  background: #000d30;
  border-radius: 20px;
  min-width: 30%;
  text-align: left;
  padding-block: 3%;
  border: 1px solid #ffffff90;
`;

const ListToMarketplaceModal = forwardRef<
  HTMLDivElement,
  ListToMarketplaceModalProps
>(
  (
    {
      theme,
      song,
      showListToMarketplaceModal,
      onListToMarketplaceCloseClick,
      onListSuccess,
    },
    ref
  ) => {
    const auth = useAuth();
    const [validInputEth, setValidInputEth] = useState(true);
    const decimalNumberRegex = /^\d*\.?\d+$/;
    const [ethPrice, setEthPrice] = useState("");
    const [placingOrder, setPlacingOrder] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [progressText, setProgressText] = useState('');
    const isMainnet = process.env.REACT_APP_USE_POLYGON_MAINNET === "true";

    const createOrder = async (provider) => {
      const seller = await provider.getSigner().getAddress();
      const seaport = new Seaport(provider);
      const standardCreateOrderInput: CreateOrderInput = {
        offer: [
          {
            itemType: 2,
            token: song.nft_address,
            identifier: "1",
          },
        ],
        // Use wrapped ether instead of the native currency on chain

        consideration: [
          {
            amount: ethers.utils.parseEther(ethPrice).toString(),
            token: isMainnet ? wrappedEthMainnetAddress : wrappedEthTestNetAddress,
            recipient: seller,
          },
        ],
        fees: [
          {
            recipient: process.env.REACT_APP_COMPOSENFT_BANK_ADDRESS,
            basisPoints: parseInt(process.env.REACT_APP_NFT_REVENUE_FEE),
          },
        ],
      };

      const { executeAllActions } = await seaport.createOrder(
        standardCreateOrderInput,
        seller
      );

      return await executeAllActions();
    };

    const onListToMarketplace = async () => {
      if (window.ethereum) {
        setPlacingOrder(true);
        const selectedProvider = getSelectedProvider();
        const provider = new ethers.providers.Web3Provider(selectedProvider);
        let order = null;
        try {
          order = await createOrder(provider);

        } catch (error) {

          if (error.toString().includes('user rejected transaction')) {
            setErrorText("Rejected transaction, please try again");
          } else {
            setErrorText("Something went wrong, please try again");
          }
          setPlacingOrder(false);
          return;
        }
        if (order !== null) {
          const jsonData = {
            master_copy_id: song.id,
            price: ethPrice,
            user_address: auth.user.wallet_address,
            order: order,
            is_song_token: false
          };
  
          axios
            .post(
              process.env.REACT_APP_BACKEND_URL + "/create_listing",
              JSON.stringify(jsonData),
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            )
            .then((response) => {
              onListSuccess();
            })
            .catch((response) => {
            });

        }

      }
    };

    const onEthPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const num = event.target.value;
      if (!decimalNumberRegex.test(num)) {
        setValidInputEth(false);
        setEthPrice(event.target.value);
        return;
      }

      setValidInputEth(true);
      setEthPrice(event.target.value);
    };

    return (
      <Modal
        backgroundOpacity={"rgb(0,43,66,0.9)"}
        ref={ref}
        show={showListToMarketplaceModal}
      >
        <ModalContainer>
          <CloseCircle
            bg={close_circle}
            onClick={onListToMarketplaceCloseClick}
          />
          <Text bold={true} size={"1.2rem"}>
            List to Marketplace
          </Text>
          <SongCheckoutContainer>
            <SongCheckoutImg src={song.song_cover_s3_link} />
            <Column style={{ justifyContent: "center", paddingLeft: "10%" }}>
              <Text bold={true}>{song.song_name}</Text>
            </Column>
          </SongCheckoutContainer>
          <InputWithLabel>
            <RequiredEthText>Price</RequiredEthText>
            <ErrorInputWithIcon
              valid={validInputEth}
              src={eth_icon}
              onChange={onEthPriceChange}
              placeholder="enter list price here"
            />
          </InputWithLabel>
          {!validInputEth && (
            <InputErrorText>Input must be number!</InputErrorText>
          )}

          <Row align="space-between" style={{ width: '100%', marginTop: "1rem" }}>
            <CheckoutPriceContainer>
              <img src={eth_icon} />
              {ethPrice} ETH
            </CheckoutPriceContainer>
            {validInputEth && ethPrice !== "" && parseFloat(ethPrice) > 0 ? (
              <>
                {!placingOrder ? (
                  <Column 
                    style={{ marginLeft: "auto" }}>
                  <SimpleButton
                    onClick={onListToMarketplace}
                  >
                    Publish 
                  </SimpleButton>
                  <BlockChainErrorText>{errorText}</BlockChainErrorText>
                  </Column>
                ) : (
                  <Column>
                  <SimpleButton>
                    <CircularSVG>
                      <CustomCircle
                        cx="15"
                        cy="15"
                        r="10"
                        fill="none"
                        stroke-width="5"
                        stroke-miterlimit="10"
                      />
                    </CircularSVG>
                  </SimpleButton>
                  <ProgressText>{progressText}</ProgressText>
                  </Column>
                  
                )}
              </>
            ) : (
              <DisabledButton bg="primary" disabled>
                Publish
              </DisabledButton>
            )}
          </Row>
        </ModalContainer>
      </Modal>
    );
  }
);

export default ListToMarketplaceModal;
